import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { profile as profileApi } from "../lib/api";
import SmsConsentDisclosure from "./SmsConsentDisclosure";

/**
 * SMS Preferences panel on the Profile page.
 *
 * Twilio A2P 10DLC compliance:
 *   - The toggle defaults to whatever the server says the user's current
 *     consent state is (OFF for anyone who hasn't opted in).
 *   - Saving with the toggle ON requires a phone number; the server
 *     validates + normalizes to US E.164.
 *   - Turning the toggle OFF is a one-click opt-out (also honors STOP).
 */
export default function SmsPreferencesPanel() {
  const { user, setUser } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [smsOptIn, setSmsOptIn] = useState(Boolean(user?.sms_opt_in));
  const [saving, setSaving] = useState(false);

  // Keep local state in sync if user gets refreshed elsewhere (e.g. after
  // signup completes on the same session).
  useEffect(() => {
    setPhone(user?.phone || "");
    setSmsOptIn(Boolean(user?.sms_opt_in));
  }, [user?.phone, user?.sms_opt_in]);

  const dirty =
    smsOptIn !== Boolean(user?.sms_opt_in) ||
    (phone || "") !== (user?.phone || "");

  const save = async () => {
    if (smsOptIn && !phone.trim()) {
      toast.error("Enter a mobile number to opt in to SMS.");
      return;
    }
    setSaving(true);
    try {
      const updated = await profileApi.updateSmsPreferences({
        phone: phone.trim() || null,
        sms_opt_in: smsOptIn,
      });
      setUser(updated);
      toast.success(
        smsOptIn
          ? "SMS notifications enabled — reply STOP anytime to opt out."
          : "SMS notifications turned off."
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not save SMS preferences."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="sms-preferences-panel"
      className="rounded-3xl border border-[#DCE5DB] bg-white p-7 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <MessageSquare size={16} className="text-[#4F6F52]" />
        <span className="text-xs uppercase tracking-[0.16em] font-semibold text-[#4F6F52]">
          SMS notifications
        </span>
      </div>
      <p className="text-[15px] text-[#2E2E2E] leading-relaxed mb-5">
        Optional reminders, gift notifications, and reflection messages
        delivered by text. You can turn this off at any time.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm text-[#6B6B6B] mb-1.5">
            Mobile number
          </label>
          <input
            data-testid="profile-sms-phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
            placeholder="(555) 123-4567"
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            data-testid="profile-sms-consent"
            type="checkbox"
            checked={smsOptIn}
            onChange={(e) => setSmsOptIn(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#DCE5DB] text-[#4F6F52] focus:ring-[#4F6F52]/30 accent-[#4F6F52]"
          />
          <span className="text-sm text-[#2E2E2E] leading-snug">
            Yes, send me LifeGuide SMS/text messages at this number.
          </span>
        </label>

        <SmsConsentDisclosure testId="profile-sms-disclosure" />

        <button
          data-testid="profile-sms-save"
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-2.5 text-sm font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save SMS preferences"}
        </button>
      </div>
    </div>
  );
}
